// =====================================================
// Swiss Finance OS - Compliance Dashboard
// Übersicht über Datenschutz-Compliance-Status
// =====================================================

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  FileText, 
  Users, 
  Database,
  Settings,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { processingActivities, generateComplianceReport } from '@/config/processing-activities';

export const ComplianceDashboard = () => {
  const complianceReport = generateComplianceReport();
  
  // Compliance-Status berechnen
  const totalRequirements = 12;
  const completedRequirements = 8; // Basierend auf implementierten Features
  const complianceScore = Math.round((completedRequirements / totalRequirements) * 100);

  const complianceItems = [
    {
      id: 'privacy-policy',
      title: 'Datenschutzerklärung',
      status: 'completed',
      description: 'DSGVO/FADP-konforme Datenschutzerklärung implementiert',
      icon: FileText,
      link: '/privacy'
    },
    {
      id: 'processing-activities',
      title: 'Verzeichnis der Verarbeitungstätigkeiten',
      status: 'completed',
      description: 'Vollständige Dokumentation aller Datenverarbeitungsprozesse',
      icon: Database,
      link: null
    },
    {
      id: 'data-subject-rights',
      title: 'Betroffenenrechte',
      status: 'completed',
      description: 'Interface für Ausübung der Datenschutzrechte',
      icon: Users,
      link: '/data-rights'
    },
    {
      id: 'consent-management',
      title: 'Einwilligungsmanagement',
      status: 'completed',
      description: 'DSGVO-konformer Cookie-Banner und Consent-Management',
      icon: Settings,
      link: null
    },
    {
      id: 'data-anonymization',
      title: 'Datenanonymisierung',
      status: 'completed',
      description: 'Intelligente Anonymisierung für KI-Verarbeitung',
      icon: Shield,
      link: null
    },
    {
      id: 'audit-trail',
      title: 'Audit-Trail',
      status: 'completed',
      description: 'Strukturiertes Logging für Compliance-Nachweis',
      icon: FileText,
      link: null
    },
    {
      id: 'avv-contracts',
      title: 'Auftragsverarbeitungsverträge',
      status: 'pending',
      description: 'AVV mit Drittanbietern (Supabase, KI-Provider)',
      icon: FileText,
      link: null
    },
    {
      id: 'data-protection-officer',
      title: 'Datenschutzbeauftragter',
      status: 'pending',
      description: 'Bestellung und Kontaktdaten des DSB',
      icon: Users,
      link: null
    },
    {
      id: 'dpia',
      title: 'Datenschutz-Folgenabschätzung',
      status: 'in-progress',
      description: 'DSFA für KI-Verarbeitung durchgeführt',
      icon: AlertCircle,
      link: null
    },
    {
      id: 'technical-safeguards',
      title: 'Technische Maßnahmen',
      status: 'completed',
      description: 'SSL, Verschlüsselung, Zugriffskontrollen',
      icon: Shield,
      link: null
    },
    {
      id: 'data-retention',
      title: 'Speicherdauer',
      status: 'completed',
      description: 'Konforme Speicherdauer nach Schweizer Recht',
      icon: Clock,
      link: null
    },
    {
      id: 'breach-notification',
      title: 'Datenpannen-Meldung',
      status: 'pending',
      description: 'Verfahren für Meldung von Datenschutzverletzungen',
      icon: AlertCircle,
      link: null
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Abgeschlossen</Badge>;
      case 'in-progress':
        return <Badge className="bg-yellow-100 text-yellow-800">In Bearbeitung</Badge>;
      case 'pending':
        return <Badge className="bg-red-100 text-red-800">Ausstehend</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unbekannt</Badge>;
    }
  };

  const completedCount = complianceItems.filter(item => item.status === 'completed').length;
  const inProgressCount = complianceItems.filter(item => item.status === 'in-progress').length;
  const pendingCount = complianceItems.filter(item => item.status === 'pending').length;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Datenschutz-Compliance Dashboard
        </h1>
        <p className="text-gray-600">
          Übersicht über den aktuellen Status der DSGVO/FADP-Compliance
        </p>
      </div>

      {/* Compliance Score */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Compliance-Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-gray-900">{complianceScore}%</span>
              <span className="text-sm text-gray-600">
                {completedCount} von {totalRequirements} Anforderungen erfüllt
              </span>
            </div>
            <Progress value={complianceScore} className="h-3" />
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">{completedCount}</div>
                <div className="text-sm text-gray-600">Abgeschlossen</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">{inProgressCount}</div>
                <div className="text-sm text-gray-600">In Bearbeitung</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{pendingCount}</div>
                <div className="text-sm text-gray-600">Ausstehend</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verarbeitungstätigkeiten Übersicht */}
      <Card>
        <CardHeader>
          <CardTitle>Verarbeitungstätigkeiten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{complianceReport.totalActivities}</div>
              <div className="text-sm text-gray-600">Verarbeitungstätigkeiten dokumentiert</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{complianceReport.activitiesWithDPIA}</div>
              <div className="text-sm text-gray-600">DSFA erforderlich</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{complianceReport.activitiesWithThirdCountryTransfers}</div>
              <div className="text-sm text-gray-600">Drittland-Übertragungen</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance-Items */}
      <div className="grid md:grid-cols-2 gap-6">
        {complianceItems.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5 text-gray-600" />
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                  </div>
                  {getStatusIcon(item.status)}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">{item.description}</p>
                <div className="flex items-center justify-between">
                  {getStatusBadge(item.status)}
                  {item.link && (
                    <Button asChild size="sm" variant="outline">
                      <Link to={item.link}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Anzeigen
                      </Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Nächste Schritte */}
      <Card>
        <CardHeader>
          <CardTitle>Nächste Schritte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-yellow-800 mb-2">Priorität 1 (Diese Woche)</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• AVV mit Supabase und KI-Providern abschließen</li>
                <li>• Datenschutzbeauftragten bestellen und Kontaktdaten aktualisieren</li>
                <li>• DSFA für KI-Verarbeitung finalisieren</li>
              </ul>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Priorität 2 (Nächste Woche)</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Verfahren für Datenpannen-Meldung implementieren</li>
                <li>• Regelmäßige Compliance-Audits einrichten</li>
                <li>• Mitarbeiterschulungen zum Datenschutz durchführen</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Kontakt */}
      <Card>
        <CardHeader>
          <CardTitle>Datenschutz-Kontakt</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Datenschutzbeauftragter</h4>
              <p className="text-sm text-gray-600">
                E-Mail: datenschutz@togeco.ch<br/>
                Telefon: +41 XX XXX XX XX
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Aufsichtsbehörde</h4>
              <p className="text-sm text-gray-600">
                Eidgenössischer Datenschutz- und Öffentlichkeitsbeauftragter (EDÖB)<br/>
                Feldeggweg 1, 3003 Bern
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceDashboard;
